/**
 * teeyoung.js — Engine sinh SEO title/tags/description theo công thức Teeyoung.
 * Rule-based, chạy offline. Không phụ thuộc DOM. Dùng được cả trong browser lẫn Node.
 *
 * Pipeline 1 task:
 *   parseSource(sp)          -> { design, noun, modifier, occasion, recipient, ... }
 *   generateTitles(sp)       -> 3 title theo formula Teeyoung (8–13 từ, ≤140 ký tự)
 *   generateTagCandidates    -> pool tag ứng viên (>13) để bước Ytrends lọc
 *   selectTags(cands, mByTag)-> chốt ĐÚNG 13 tag (offline hoặc theo Ytrends)
 *   generateDescription(sp)  -> mô tả theo format emoji chuẩn
 *
 * XEM HANDOFF.md để biết đầy đủ rule Teeyoung + shape API Matrixty + Ytrends.
 */

const TITLE_MAX_CHARS = 140;
const TITLE_MIN_WORDS = 8;
const TITLE_MAX_WORDS = 13;
const TAG_COUNT = 13;
const TAG_MAX_CHARS = 20;

// ---- Từ điển nhận dạng ----------------------------------------------------

// promo / giá / khuyến mãi -> xoá hẳn khỏi title & tag
const PROMO_WORDS = /\b(new!?|sale|best\s?sellers?|hot|limited(?:\s+edition)?|free\s+shipping|trending|top\s+rated|\$?\d+(?:\.\d+)?\s?(?:usd|off)?%?)\b/gi;
// tính từ cảm xúc -> đẩy xuống description, không nhét title
const ADJECTIVES = /\b(beautiful|cute|lovely|gorgeous|stunning|amazing|perfect|trendy|preppy|adorable|unique|charming|elegant)\b/gi;
// hàng số hoá / digital -> KHÔNG dùng cho sản phẩm vật lý
const DIGITAL_TAG = /\b(svg|png|dxf|eps|pdf|digital|printable|cricut|sublimation|clipart|vector|cut\s?file)\b/i;
// màu / size -> là attribute, KHÔNG cho vào tag
const COLOR_SIZE = /\b(red|blue|green|black|white|pink|yellow|purple|orange|grey|gray|navy|beige|small|medium|large|xl|xxl|2xl|3xl|\d{1,2}\s?oz|s\/m|m\/l)\b/i;
// ghép Custom + Personalized trong cùng cụm -> vi phạm rule
const HAS_CUSTOM = /\bcustom(?:ized|ised)?\b/i;
const HAS_PERSONALIZED = /\bpersonali[sz]ed?\b/i;

/**
 * variant Matrixty -> danh từ sản phẩm + modifier ổn định (material/size).
 * modifier chỉ đặt khi nó là thuộc tính CỐ ĐỊNH của item (Mug 11oz, Tumbler 20oz).
 * Với hàng may mặc, size/màu thay đổi theo đơn -> để trống, xử lý ở attributes.
 */
const VARIANT_MAP = {
  'shirt':             { noun: 'Shirt',      modifier: '' },
  'family shirt':      { noun: 'Shirt',      modifier: '' },
  't-shirt':           { noun: 'Shirt',      modifier: '' },
  'tee':               { noun: 'Shirt',      modifier: '' },
  'sweatshirt':        { noun: 'Sweatshirt', modifier: '' },
  'hoodie':            { noun: 'Hoodie',     modifier: '' },
  'mug':               { noun: 'Mug',        modifier: '11oz' },
  'coffee mug':        { noun: 'Mug',        modifier: '11oz' },
  'family coffee mug': { noun: 'Mug',        modifier: '11oz' },
  'tumbler':           { noun: 'Tumbler',    modifier: '20oz' },
  'blanket':           { noun: 'Blanket',    modifier: '' },
  'ornament':          { noun: 'Ornament',   modifier: '' },
  'poster':            { noun: 'Poster',     modifier: '' },
  'canvas':            { noun: 'Canvas',     modifier: '' },
  'pillow':            { noun: 'Pillow',     modifier: '' },
};

// Occasion: phrase title-friendly + essence=true khi occasion là bản chất item.
const OCCASIONS = [
  { re: /\bbirthday\b/i,                     phrase: 'Birthday Gift',      essence: true  },
  { re: /\b(wedding|his\s*&?\s*hers)\b/i,    phrase: 'Wedding Gift',       essence: true  },
  { re: /\banniversar/i,                     phrase: 'Anniversary Gift',   essence: true  },
  { re: /\b(christmas|xmas)\b/i,             phrase: 'Christmas Gift',     essence: true  },
  { re: /\bvalentine/i,                      phrase: "Valentine's Gift",   essence: true  },
  { re: /\bmother'?s?\s*day\b/i,             phrase: "Mother's Day Gift",  essence: true  },
  { re: /\bfather'?s?\s*day\b/i,             phrase: "Father's Day Gift",  essence: true  },
  { re: /\bgraduation\b/i,                   phrase: 'Graduation Gift',    essence: true  },
  { re: /\bretirement\b/i,                   phrase: 'Retirement Gift',    essence: true  },
  { re: /\bhousewarming\b/i,                 phrase: 'Housewarming Gift',  essence: true  },
  { re: /\bbaby\s*shower\b/i,                phrase: 'Baby Shower Gift',   essence: true  },
  { re: /\b(memorial|remembrance|in\s*loving\s*memory)\b/i, phrase: 'Memorial Gift', essence: true },
  { re: /\bhalloween\b/i,                    phrase: 'Halloween Gift',     essence: false },
  { re: /\bthanksgiving\b/i,                 phrase: 'Thanksgiving Gift',  essence: false },
];

// Recipient: phrase + short tag variant.
const RECIPIENTS = [
  { re: /\bdog\s*dad\b/i,      title: 'Dog Dad',      tag: 'dog dad gift' },
  { re: /\bdog\s*mom\b/i,      title: 'Dog Mom',      tag: 'dog mom gift' },
  { re: /\bcat\s*dad\b/i,      title: 'Cat Dad',      tag: 'cat dad gift' },
  { re: /\bcat\s*mom\b/i,      title: 'Cat Mom',      tag: 'cat mom gift' },
  { re: /\bgrand(ma|mother|mom)\b/i, title: 'Grandma', tag: 'grandma gift' },
  { re: /\bgrand(pa|father|dad)\b/i, title: 'Grandpa', tag: 'grandpa gift' },
  { re: /\b(dad|father|papa)\b/i, title: 'Dad',       tag: 'gift for dad' },
  { re: /\b(mom|mother|mama)\b/i, title: 'Mom',       tag: 'gift for mom' },
  { re: /\bwife\b/i,           title: 'Wife',         tag: 'gift for wife' },
  { re: /\bhusband\b/i,        title: 'Husband',      tag: 'gift for husband' },
  { re: /\bgirlfriend\b/i,     title: 'Girlfriend',   tag: 'girlfriend gift' },
  { re: /\bboyfriend\b/i,      title: 'Boyfriend',    tag: 'boyfriend gift' },
  { re: /\bteacher\b/i,        title: 'Teacher',      tag: 'teacher gift' },
  { re: /\bnurse\b/i,          title: 'Nurse',        tag: 'nurse gift' },
  { re: /\bcouple/i,           title: 'Couple',       tag: 'couple gift' },
  { re: /\bfamily\b/i,         title: 'Family',       tag: 'family gift' },
  { re: /\bsister\b/i,         title: 'Sister',       tag: 'sister gift' },
  { re: /\bbrother\b/i,        title: 'Brother',      tag: 'brother gift' },
  { re: /\bfriend\b/i,         title: 'Best Friend',  tag: 'best friend gift' },
  { re: /\b(him|men|man)\b/i,  title: 'Him',          tag: 'gift for him' },
  { re: /\b(her|women|woman)\b/i, title: 'Her',       tag: 'gift for her' },
];

const STOPWORDS = new Set(['the','a','an','and','or','for','with','your','you','our','of','to','in','on',
  'gift','gifts','custom','customized','personalized','personalised','new','this','that','from','by']);

const BRAND_IP = /\b(university|college|nfl|nba|mlb|nhl|disney|marvel|pixar|nike|adidas|harry\s*potter|star\s*wars|pokemon|taylor\s*swift|bluey|sanrio|hello\s*kitty)\b/i;

// ---- Helpers --------------------------------------------------------------

function wordCount(s) { return String(s).trim().split(/\s+/).filter(Boolean).length; }
function titleCase(s) {
  return String(s).toLowerCase().replace(/\b([a-z])/g, (m, c) => c.toUpperCase())
    .replace(/'S\b/g, "'s");
}
function stripPromo(s = '') {
  return String(s).replace(PROMO_WORDS, ' ').replace(ADJECTIVES, ' ').replace(/\s{2,}/g, ' ').trim();
}

/** Ghép các cụm -> title; cắt để đạt ≤140 ký tự & ≤13 từ, đảm bảo space sau phẩy. */
function fitTitle(parts) {
  let title = parts.filter(Boolean).join(' ').replace(/\s{2,}/g, ' ').trim();
  title = title.replace(/\s+,/g, ',').replace(/,(?=\S)/g, ', ').replace(/,\s*,/g, ', ');
  while ((title.length > TITLE_MAX_CHARS || wordCount(title) > TITLE_MAX_WORDS) && title.includes(' ')) {
    title = title.slice(0, title.lastIndexOf(' ')).trim().replace(/[,]+$/, '').trim();
  }
  return title.replace(/[,\s]+$/, '').trim();
}

/** Bỏ trùng từ liền kề không phân biệt hoa thường (vd "Mug Mug", "Dog Dog"). */
function dedupeWords(s) {
  return String(s).replace(/\b(\w+)(\s+\1\b)+/gi, '$1');
}

// ---- Parser ---------------------------------------------------------------

/**
 * Rút các thành phần title từ scrapedProduct.
 * design = phần "Design/Quote" (tên object/theme/quote) đã sạch promo, bỏ noun & recipient/occasion.
 */
function parseSource(sp = {}) {
  const rawTitle = String(sp.productTitle || '').trim();
  const niche = String(sp.niche || '').trim();

  // 1. Noun + modifier từ variant (fallback: dò trong title).
  const vkey = String(sp.variant || '').toLowerCase().trim();
  let vm = VARIANT_MAP[vkey];
  if (!vm) {
    const hit = Object.keys(VARIANT_MAP).find(k => new RegExp(`\\b${k}\\b`, 'i').test(rawTitle));
    vm = hit ? VARIANT_MAP[hit] : { noun: 'Gift', modifier: '' };
  }
  const noun = vm.noun;
  const modifier = vm.modifier;

  // 2. Occasion.
  const occHit = OCCASIONS.find(o => o.re.test(rawTitle) || o.re.test(niche));
  const occasion = occHit ? occHit.phrase : '';
  const occasionEssence = occHit ? occHit.essence : false;

  // 3. Recipient (ưu tiên cụm cụ thể như "dog dad" đứng trước "dad").
  const recHit = RECIPIENTS.find(r => r.re.test(rawTitle) || r.re.test(niche));
  const recipient = recHit ? recHit.title : '';
  const recipientTag = recHit ? recHit.tag : '';

  // 4. Design/Quote: title gốc, bỏ đuôi "- Personalized...", promo, noun, recipient/occasion literal.
  let design = rawTitle.split(/\s*[-–—|]\s*(?:personali[sz]ed|custom)/i)[0] || rawTitle;
  design = stripPromo(design);
  // bỏ chính danh từ + biến thể để tránh lặp
  design = design.replace(new RegExp(`\\b(${noun}s?|t-?shirts?|tees?|coffee)\\b`, 'gi'), ' ');
  // bỏ literal occasion/recipient khỏi design (chúng được đặt riêng ở cụm cuối / tags)
  OCCASIONS.forEach(o => { design = design.replace(new RegExp(o.re.source, 'gi'), ' '); });
  RECIPIENTS.forEach(r => { design = design.replace(new RegExp(r.re.source, 'gi'), ' '); });
  // bỏ personalize/custom (ta tự thêm)
  design = design.replace(/\b(personali[sz]ed?|customi[sz]ed?|custom)\b/gi, ' ');
  design = design.replace(/[^\w'&\s-]/g, ' ')
                 .replace(/\s+[-&]+\s+/g, ' ')      // bỏ dấu "-" / "&" đứng lẻ
                 .replace(/\s{2,}/g, ' ').trim();
  design = dedupeWords(design);

  // rút gọn design còn tối đa ~5 từ ý nghĩa; loại token 1 ký tự / rác
  let designWords = design.split(/\s+/)
    .filter(w => w && w.length > 1 && /[a-z]/i.test(w) && !STOPWORDS.has(w.toLowerCase()));
  design = titleCase(designWords.slice(0, 5).join(' ')).trim();

  // Design slot KHÔNG được rỗng -> fallback theo thứ tự: niche -> recipient -> noun theme.
  if (!design) {
    design = niche ? titleCase(stripPromo(niche).split(/\s+/).slice(0, 3).join(' '))
           : recipient ? recipient
           : 'Custom Design';
  }

  return {
    design, noun, modifier, occasion, occasionEssence, recipient, recipientTag,
    niche, designWords: designWords.map(w => w.toLowerCase()),
    brandRisk: BRAND_IP.test(rawTitle),
    sourceHasTags: Array.isArray(sp.tags) && sp.tags.length >= 8, // nguồn drive.google đã có 13 tag
  };
}

// ---- TITLE ----------------------------------------------------------------

/** Đảm bảo title ≥ TITLE_MIN_WORDS bằng cách nối thêm cụm bổ sung (không lẫn Custom+Personalized). */
function padTitle(title, extras) {
  let out = title;
  for (const ex of extras) {
    if (wordCount(out) >= TITLE_MIN_WORDS) break;
    if (!ex) continue;
    if (out.toLowerCase().includes(ex.toLowerCase())) continue;
    const candidate = fitTitle([out + ',', ex]);
    if (wordCount(candidate) <= TITLE_MAX_WORDS) out = candidate;
  }
  return out;
}

/** Gỡ vi phạm Custom+Personalized: nếu title chứa cả hai, bỏ "Custom". */
function fixCustomClash(title) {
  if (HAS_CUSTOM.test(title) && HAS_PERSONALIZED.test(title)) {
    return title.replace(/\bcustom(?:i[sz]ed)?\b\s*/i, '').replace(/\s{2,}/g, ' ').trim();
  }
  return title;
}

/**
 * Sinh 3 title khác cấu trúc theo formula:
 *   Personalize + Design/Quote + Noun + modifier + (Occasion HOẶC Recipient) ở cuối.
 * Cụm cuối chỉ một trong hai; cái còn lại đẩy xuống tags.
 */
function generateTitles(sp = {}) {
  const p = parseSource(sp);
  const { design, noun, modifier, occasion, occasionEssence, recipient } = p;
  const nounMod = `${noun}${modifier ? ' ' + modifier : ''}`;

  // Cụm cuối: ưu tiên occasion khi nó là bản chất item; nếu không thì recipient.
  const endPrimaryOcc = occasionEssence && occasion;
  const endA = endPrimaryOcc ? occasion : (recipient ? `Gift for ${recipient}` : occasion || 'Gift');
  const endB = endPrimaryOcc ? (recipient ? `Gift for ${recipient}` : '')
                             : (occasion || (recipient ? `${recipient} Gift` : ''));

  const extras = [
    recipient ? `Gift for ${recipient}` : '',
    occasion,
    p.niche ? titleCase(p.niche.split(/\s+/).slice(0, 2).join(' ')) + ' Gift' : '',
    'Gift for Him', 'Gift for Her', 'Birthday Gift', 'Unique Gift Idea', 'Gift Idea',
  ];

  // Title 1 — Personalized dẫn đầu, kết bằng cụm cuối chính.
  let t1 = fitTitle([`Personalized ${design} ${nounMod}`, endA]);
  t1 = padTitle(t1, extras);

  // Title 2 — dùng Custom (KHÔNG Personalized), kết bằng cụm còn lại.
  let t2 = fitTitle([`Custom ${design} ${nounMod}`, endB || endA]);
  t2 = fixCustomClash(t2);
  t2 = padTitle(t2, extras.filter(e => !/custom/i.test(e)));

  // Title 3 — cấu trúc recipient-first / design-cuối, tránh trùng t1/t2.
  let t3;
  if (recipient) {
    t3 = fitTitle([`Personalized ${nounMod} for ${recipient}`, `${design} Design`, endPrimaryOcc ? occasion : '']);
  } else {
    t3 = fitTitle([`${design} ${nounMod}`, 'Personalized Gift', occasion]);
  }
  t3 = fixCustomClash(padTitle(t3, extras));

  const out = [t1, t2, t3]
    .map(t => dedupeWords(t).replace(/\s{2,}/g, ' ').trim())
    .filter(Boolean);
  return [...new Set(out)];
}

// ---- TAGS -----------------------------------------------------------------

/** Chuẩn hoá 1 tag: lowercase, gọn, ≤20 ký tự; loại digital/color-size. Trả '' nếu không hợp lệ. */
function normalizeTag(t = '') {
  let tag = String(t).toLowerCase().replace(/[^\w'&\s-]/g, ' ').replace(/\s{2,}/g, ' ').trim();
  if (!tag || tag.length > TAG_MAX_CHARS) return '';
  if (DIGITAL_TAG.test(tag)) return '';
  // màu/size là attribute, KHÔNG cho vào tag -> loại tag chứa bất kỳ token màu/size
  if (tag.split(/\s+/).some(w => COLOR_SIZE.test(w))) return '';
  return tag;
}

/**
 * Pool tag ứng viên (thường > 13) để bước selectTags/Ytrends lọc.
 * Non-title keyword (gift for him/her, long-tail, synonym) -> tags.
 */
function generateTagCandidates(sp = {}) {
  const p = parseSource(sp);
  const noun = p.noun.toLowerCase();
  const dwords = p.designWords.filter(w => w.length > 2 && !STOPWORDS.has(w));
  const two = dwords.slice(0, 2).join(' ');

  const seeds = [
    ...(sp.tags || []),                       // giữ tag nguồn (drive.google) làm ứng viên
    `personalized ${noun}`,
    `custom ${noun}`,
    `${noun} gift`,
    p.recipientTag,
    two && `${two} ${noun}`,
    ...dwords.slice(0, 4).map(w => `${w} ${noun}`),
    ...dwords.slice(0, 3).map(w => `${w} gift`),
    p.recipient && `${p.recipient.toLowerCase()} ${noun}`,
    p.occasion && `${p.occasion.toLowerCase().replace(/ gift$/, '')} ${noun}`,
    p.occasion && p.occasion.toLowerCase(),
    p.niche && `${p.niche.toLowerCase().split(/\s+/).slice(0, 2).join(' ')} gift`,
    'gift for him', 'gift for her',
    `${noun} for dad`, `${noun} for mom`,
    `cute ${noun}`, `${noun} lover gift`,
  ];

  const seen = new Set();
  const out = [];
  for (const s of seeds) {
    const n = normalizeTag(s);
    if (n && !seen.has(n)) { seen.add(n); out.push(n); }
  }
  return out;
}

// ---- Ytrends scoring & selection -----------------------------------------

/**
 * Map competition_level Ytrends -> màu competitor (theo HANDOFF).
 *   low  -> xanh (ít cạnh tranh, dễ top)
 *   medium -> vàng
 *   high -> đỏ (cực dày, tránh)
 */
function competitorColor(m = {}) {
  const lvl = String(m.competition_level || '').toLowerCase();
  if (lvl === 'low') return 'green';
  if (lvl === 'high') return 'red';
  if (lvl === 'medium') return 'yellow';
  // fallback theo số listing trong subset nếu không có competition_level
  const n = Number(m.total_listings);
  if (Number.isFinite(n)) {
    if (n > 0 && n < 300) return 'green';
    if (n < 3000) return 'yellow';
    return 'red';
  }
  return 'unknown';
}

/**
 * Điểm 1 tag khi CÓ metrics Ytrends. Cao = nên dùng.
 * Kết hợp: opportunity_score (composite) + màu competitor + thứ hạng rank + volume.
 * Ưu tiên competitor xanh + volume trung bình; tránh mạnh competitor đỏ.
 */
function scoreTagYtrends(tag, m = {}) {
  let score = Number.isFinite(+m.opportunity_score) ? +m.opportunity_score : 40;
  const color = competitorColor(m);
  score += ({ green: 20, yellow: 0, red: -55, unknown: -8 })[color];

  const rank = Number(m.rank);
  if (Number.isFinite(rank) && rank > 0) {
    if (rank <= 10000) score += 18;
    else if (rank <= 50000) score += 8;
    else score += 2;
  } else {
    score -= 6; // unranked / insufficient data -> demand chưa rõ
  }

  // volume proxy: bán/lượt xem trung bình
  const vol = Number(m.avg_total_sold) || Number(m.avg_views) || 0;
  if (vol >= 10) score += 8; else if (vol >= 2) score += 4;

  // tag từ nguồn "MUST_USE" của related_keywords -> tin cậy cao
  if (/must_use/i.test(m.action_reason || '')) score += 12;

  return { tag, score: Math.round(score), color, rank: Number.isFinite(rank) ? rank : null,
           opportunity: Number.isFinite(+m.opportunity_score) ? +m.opportunity_score : null };
}

/** Điểm heuristic offline (khi chưa có Ytrends) — vẫn cho ra 13 tag hợp lý. */
function scoreTagOffline(tag, ctx) {
  let score = 50;
  const w = tag.split(/\s+/);
  if (w.length === 2 || w.length === 3) score += 12;   // long-tail vừa phải tốt nhất
  if (w.length === 1) score -= 15;                     // 1 từ quá rộng
  if (tag.includes(ctx.noun)) score += 8;
  if (ctx.dwords.some(d => tag.includes(d))) score += 10;
  if (/gift for (him|her)|for (dad|mom)/.test(tag)) score += 6;
  if (tag.length <= 18) score += 3;
  return { tag, score, color: 'unknown', rank: null, opportunity: null };
}

/**
 * Chốt ĐÚNG 13 tag từ pool ứng viên.
 * @param candidates string[]  pool đã sinh offline
 * @param metricsByTag { [tag]: ytrendsMetrics } | null   nếu có -> lọc theo Ytrends
 * @param ctx { noun, dwords }  context để chấm offline
 * Luôn trả đúng 13 (nếu pool thiếu sẽ độn thêm biến thể an toàn), unique, ≤20 ký tự.
 */
function selectTags(candidates, metricsByTag, ctx = {}) {
  const noun = (ctx.noun || 'gift').toLowerCase();
  const dwords = ctx.dwords || [];
  const seen = new Set();
  const pool = [];
  for (const c of candidates || []) {
    const n = normalizeTag(c);
    if (n && !seen.has(n)) { seen.add(n); pool.push(n); }
  }

  const scored = pool.map(tag => {
    const m = metricsByTag && metricsByTag[tag];
    return m ? scoreTagYtrends(tag, m) : scoreTagOffline(tag, { noun, dwords });
  });

  // Nếu có Ytrends: loại thẳng tag đỏ khi vẫn đủ tag khác thay thế.
  let ranked = scored.sort((a, b) => b.score - a.score);
  if (metricsByTag) {
    const nonRed = ranked.filter(t => t.color !== 'red');
    if (nonRed.length >= TAG_COUNT) ranked = nonRed;
  }

  const chosen = ranked.slice(0, TAG_COUNT).map(t => t.tag);

  // Độn cho đủ 13 nếu pool thiếu (biến thể an toàn, không trùng, ≤20 ký tự).
  const fillers = [
    `${noun} gift`, `custom ${noun}`, `${noun} for dad`, `${noun} for mom`,
    `cute ${noun}`, `${noun} lover gift`, `unique ${noun}`,
    ...dwords.map(d => `${d} gift`), ...dwords.map(d => `${d} ${noun}`),
    'gift for him', 'gift for her', 'gift for dad', 'gift for mom',
    'birthday gift', 'christmas gift', 'cute gift idea', 'unique gift idea',
  ];
  for (const f of fillers) {
    if (chosen.length >= TAG_COUNT) break;
    const n = normalizeTag(f);
    if (n && !chosen.includes(n)) chosen.push(n);
  }
  return chosen.slice(0, TAG_COUNT);
}

/** Danh sách keyword cần tra Ytrends cho 1 task (dùng cho nút Validate). */
function tagsToValidate(sp) { return generateTagCandidates(sp); }

// ---- DESCRIPTION ----------------------------------------------------------

function generateDescription(sp = {}) {
  const p = parseSource(sp);
  const design = p.design;
  const noun = p.noun.toLowerCase();
  const adj = (String(sp.productTitle || '').match(ADJECTIVES)?.[0] || 'beautifully designed').toLowerCase();
  const forWhom = p.recipient ? ` — a heartfelt gift for ${p.recipient}` : '';
  const occLine = p.occasion ? p.occasion.replace(/ gift$/i, '') : 'birthdays, holidays and special moments';

  return `This ${adj} personalized ${design} ${noun}${forWhom}. Add a name to make it truly one of a kind.

📏 DETAILS
• Made to order with premium materials
• Multiple sizes and colors available
• Personalization: add your name/text at checkout

🎁 PERFECT FOR
Thoughtful gifts for loved ones — ${occLine}.

🧼 CARE
Follow the care label; handle the print/design gently to keep colors vivid.

📦 PACKAGE
Made to order and carefully packed, shipped within 1–5 business days.

⚠️ PLEASE NOTE
Colors may vary slightly depending on your screen settings.`;
}

// ---- Pipeline -------------------------------------------------------------

/** Xử lý 1 row Matrixty. Bỏ qua nếu status === 'done'. */
function processTask(row) {
  if (!row || row.status === 'done') return null;
  const sp = row.scrapedProduct || {};
  const p = parseSource(sp);
  const candidates = generateTagCandidates(sp);
  const ctx = { noun: p.noun.toLowerCase(), dwords: p.designWords };
  return {
    id: row.id,
    sku: sp.sku,
    status: row.status,
    website: sp.website,
    variant: sp.variant,
    sourceTitle: sp.productTitle,
    titles: generateTitles(sp),
    tagCandidates: candidates,
    tags: selectTags(candidates, null, ctx), // 13 tag offline (chưa Ytrends)
    ytrendsReady: false,                     // true sau khi re-rank với metrics
    description: generateDescription(sp),
    copyrightRisk: p.brandRisk,
    _ctx: ctx,                               // để re-rank khi có Ytrends
  };
}

if (typeof module !== 'undefined') {
  module.exports = {
    parseSource, generateTitles, generateTagCandidates, selectTags,
    scoreTagYtrends, scoreTagOffline, competitorColor, normalizeTag,
    generateDescription, processTask, tagsToValidate,
    TITLE_MAX_CHARS, TITLE_MIN_WORDS, TITLE_MAX_WORDS, TAG_COUNT, TAG_MAX_CHARS,
  };
}

// --- ESM exports (dùng cho React/Vite; khối CJS ở trên bị bỏ qua khi chạy ESM) ---
export {
  parseSource, generateTitles, generateTagCandidates, selectTags,
  scoreTagYtrends, scoreTagOffline, competitorColor, normalizeTag,
  generateDescription, processTask, tagsToValidate,
  TITLE_MAX_CHARS, TITLE_MIN_WORDS, TITLE_MAX_WORDS, TAG_COUNT, TAG_MAX_CHARS,
};
